import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface IdsAccordionProps {
  control: Control<UserFormData>;
  disabled?: boolean;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
}

export default function IdsAccordion({
  control,
  errors,
  disabled,
  watch,
}: IdsAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'externalIds',
  });

  const menuItems = [
    { value: 'account', label: 'Account' },
    { value: 'custom', label: 'Custom' },
    { value: 'customer', label: 'Customer' },
    // { value: 'login_id', label: 'Login ID' },
    { value: 'network', label: 'Network' },
    { value: 'organization', label: 'Organization' },
  ];

  const externalIdsErrors = errors['externalIds'];
  const externalIdsCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({ type: '', customType: '', value: '' });
  };

  return (
    <SharedProfileInformationAccordion
      title="ID's"
      hasError={!!externalIdsErrors}
      count={externalIdsCount}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`externalIds.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`externalIds.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `externalIds-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `externalIds-type-${index}`,
                      }}
                      name="externalIds"
                      select
                      disabled={disabled}
                      label="Type"
                      error={!!externalIdsErrors?.[index]?.type}
                      helperText={
                        externalIdsErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`externalIds.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `externalIds-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `externalIds-customType-${index}`,
                        }}
                        disabled={disabled}
                        name="externalIds"
                        label="Custom Type"
                        error={!!externalIdsErrors?.[index]?.customType}
                        helperText={
                          externalIdsErrors?.[index]?.customType
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`externalIds.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      error={!!externalIdsErrors?.[index]?.value}
                      helperText={
                        externalIdsErrors?.[index]?.value
                          ? 'This field is required.'
                          : ''
                      }
                      name="externalIds"
                      disabled={disabled}
                      autoComplete="off"
                      label="Value"
                      sx={{
                        minWidth: 225,
                        textTransform: 'capitalize',
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={externalIdsCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
